/**
 * Vacation — days the streak is not judged on.
 *
 * Deliberately not part of the streak settings form below it. Everything in
 * that form is a preference you set once and forget; a pause is an event with
 * dates, and it saves the moment you declare it rather than when you remember
 * to press Save at the bottom of a page. Somebody adding a holiday from an
 * airport should not lose it to an unsubmitted form.
 *
 * Past pauses stay listed. They are the audit trail that keeps the streak
 * honest: a number with a fortnight excused inside it should be able to show
 * you which fortnight.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Palmtree, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { StreakPause } from '@/core/types';
import { api } from '@/lib/api';

export function PauseSection() {
  const queryClient = useQueryClient();
  const pausesQuery = useQuery({
    queryKey: ['streak', 'pauses'],
    queryFn: api.streakPauses,
  });

  const today = pausesQuery.data?.today ?? '';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['streak'] });
    queryClient.invalidateQueries({ queryKey: ['apps'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createStreakPause({
        from_date: from || today,
        to_date: to || null,
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      setReason('');
      setTo('');
      refresh();
      toast.success('Streak paused', {
        description: 'Those days will not count against you.',
      });
    },
    onError: (err: Error) => toast.error('Could not pause', { description: err.message }),
  });

  const endToday = useMutation({
    mutationFn: (id: number) => api.updateStreakPause(id, { to_date: today }),
    onSuccess: () => {
      refresh();
      toast.success('Welcome back');
    },
    onError: (err: Error) => toast.error('Could not end the pause', { description: err.message }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteStreakPause(id),
    onSuccess: () => {
      refresh();
      toast.success('Pause removed', { description: 'Those days are judged again.' });
    },
    onError: (err: Error) => toast.error('Could not remove it', { description: err.message }),
  });

  const pauses = pausesQuery.data?.pauses ?? [];
  const active = pauses.find((p) => isActive(p, today));

  return (
    <section className="flex flex-col gap-4">
      <div>
        <Label className="text-base">Vacation</Label>
        <p className="text-muted-foreground text-xs">
          Days you have declared off. They do not extend your streak and they do not break it — and
          they never spend a freeze. A session done on one of them still counts.
        </p>
      </div>

      {active && (
        <div className="border-success/30 bg-success/5 flex flex-wrap items-center gap-3 rounded-md border p-3">
          <Palmtree className="text-success size-5 shrink-0" aria-hidden />
          <div className="flex-1">
            <p className="text-sm font-medium">
              Paused since {active.from_date}
              {active.to_date ? ` · back on ${nextDay(active.to_date)}` : ' · no return date'}
            </p>
            {active.reason && <p className="text-muted-foreground text-xs">{active.reason}</p>}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={endToday.isPending}
            onClick={() => endToday.mutate(active.id)}
          >
            End today
          </Button>
        </div>
      )}

      <form
        className="border-border bg-card flex flex-col gap-3 rounded-md border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-muted-foreground text-xs">From</Label>
            <Input
              type="date"
              value={from || today}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-muted-foreground text-xs">Until (inclusive)</Label>
            <Input
              type="date"
              value={to}
              min={from || today}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 w-40"
            />
          </div>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Pause'}
          </Button>
        </div>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional) — Brazil, sick, exams…"
          maxLength={80}
          className="h-9"
        />
        <p className="text-muted-foreground text-xs">
          {/* The start date defaults to today but accepts the past on purpose: the
              streak only recomputes when a session finishes, so a holiday that has
              already started has not cost anything yet. */}
          You can start a pause in the past — nothing is lost until your next finished session.
          Leaving the end date empty pauses indefinitely, which is easy to forget about.
        </p>
      </form>

      {pausesQuery.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}

      {pauses.length > 0 && (
        <ul className="flex flex-col gap-2">
          {pauses.map((pause) => (
            <li
              key={pause.id}
              className="border-border flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
            >
              <span className="tabular-nums">
                {pause.from_date} → {pause.to_date ?? 'open'}
              </span>
              <span className="text-muted-foreground text-xs">
                {dayCount(pause, today)} day{dayCount(pause, today) === 1 ? '' : 's'}
                {pause.reason ? ` · ${pause.reason}` : ''}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive ml-auto"
                disabled={remove.isPending}
                onClick={() => remove.mutate(pause.id)}
                aria-label="Remove this pause"
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function isActive(pause: StreakPause, today: string): boolean {
  return today >= pause.from_date && (pause.to_date === null || today <= pause.to_date);
}

/** Both ends are inclusive, so a 12th-to-25th holiday is fourteen days. */
function dayCount(pause: StreakPause, today: string): number {
  const end = pause.to_date ?? today;
  const ms =
    new Date(end + 'T00:00:00Z').getTime() - new Date(pause.from_date + 'T00:00:00Z').getTime();
  return Math.max(0, Math.round(ms / 86_400_000) + 1);
}

/** The first day back — the day after an inclusive end date. */
function nextDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
